// Shopping-cart store — a tiny localStorage-backed pub/sub the whole
// app shares (the header badge, the feed "add" buttons, and the
// checkout modal all read/write the same state).
//
// Cart items are MINIMAL display snapshots: postId, title, image,
// price, fee/cashback %, qty, variant. They're only for showing the
// cart and a rough total. At checkout the server re-loads each post
// and re-snapshots price + FX (see POST /api/orders/cart), so a stale
// cart price never becomes the billed price.
//
// Identity of a line = postId + variant, so the same item in two
// variants ("M, blue" vs "L, red") are distinct lines, and adding the
// same variant again just bumps qty.
import { useState, useEffect } from "react";

const KEY = "cg_cart_v1";
const listeners = new Set();

function load() {
  try {
    const v = JSON.parse(localStorage.getItem(KEY) || "[]");
    return Array.isArray(v) ? v : [];
  } catch {
    return [];
  }
}

let state = load();

function persist(next) {
  state = next;
  try { localStorage.setItem(KEY, JSON.stringify(state)); } catch {}
  listeners.forEach((l) => l(state));
}

const sameLine = (x, postId, variant) =>
  x.postId === postId && (x.variant || "") === (variant || "");

export function getCart() {
  return state;
}

export function cartCount() {
  return state.reduce((n, x) => n + (x.qty || 1), 0);
}

// Add a post (or bump qty if the same variant is already in the cart).
export function cartAdd(post, { qty = 1, variant = "" } = {}) {
  if (!post || !post.id) return;
  const v = (variant || "").trim();
  const existing = state.find((x) => sameLine(x, post.id, v));
  if (existing) {
    persist(
      state.map((x) =>
        sameLine(x, post.id, v)
          ? { ...x, qty: Math.max(1, Math.min(1000, (x.qty || 1) + qty)) }
          : x,
      ),
    );
    return;
  }
  persist([
    ...state,
    {
      postId: post.id,
      title: post.title || "",
      image: post.images?.[0]?.url || null,
      priceCents: post.priceCents ?? null,
      priceRubCents: post.priceRubCents ?? null,
      organizerFeePct: post.channel?.organizerFeePct ?? 0,
      cashbackPct: post.channel?.cashbackPct ?? 0,
      publicCode: post.publicCode || null,
      qty: Math.max(1, Math.min(1000, qty)),
      variant: v,
    },
  ]);
}

export function cartSetQty(postId, variant, qty) {
  persist(
    state.map((x) =>
      sameLine(x, postId, variant)
        ? { ...x, qty: Math.max(1, Math.min(1000, qty)) }
        : x,
    ),
  );
}

export function cartSetVariant(postId, oldVariant, variant) {
  persist(
    state.map((x) =>
      sameLine(x, postId, oldVariant) ? { ...x, variant: (variant || "").trim() } : x,
    ),
  );
}

export function cartRemove(postId, variant) {
  persist(state.filter((x) => !sameLine(x, postId, variant)));
}

export function cartClear() {
  persist([]);
}

export function cartHas(postId, variant = "") {
  return state.some((x) => sameLine(x, postId, variant));
}

// Hook: subscribe a component to the cart. Returns the current items.
export function useCart() {
  const [s, setS] = useState(state);
  useEffect(() => {
    const l = (next) => setS(next);
    listeners.add(l);
    // Resync in case the cart changed between module load and mount.
    setS(state);
    return () => { listeners.delete(l); };
  }, []);
  return s;
}
